/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/core/CreateArray.ts
 * @create:      2025-07-01 17:59:55.674
 * @modify:      2025-07-11 21:19:12.918
 * @version:     6.0.0
 * @times:       7
 * @lines:       79
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { TArrayItemInitializeFunction } from "../type/basic";

/**
 * Creates a new array of the specified length, filled with the provided value.
 *
 * @param length - The number of elements in the resulting array.
 * @param value - The value to fill each element of the array with.
 * @returns An array of the specified length, filled with the given value.
 * 
 * @since 1.0.5
 * @version 2025-07-02
 */
export function CreateArray<T>(length: number, value: T): T[];
/**
 * Creates an array of the specified length, initializing each element with the provided initial value
 * and an optional initialization function.
 *
 * @param length - The number of elements in the array to create.
 * @param initialValue - The initial value to assign to each element.
 * @param initFunction - A function to further initialize each array item.
 * @returns An array of type `T` with the specified length and initialized values.
 * 
 * @since 1.0.5
 * @version 2025-07-02
 */
export function CreateArray<T>(length: number, initialValue: T, initFunction: TArrayItemInitializeFunction<T>): T[];
export function CreateArray<T>(length: number, value: T, initFunction?: TArrayItemInitializeFunction<T>): T[] {
    if (length < 0) {
        throw new RangeError("Length must be a non-negative integer.");
    }

    if (length === 0) {
        return [];
    }

    const elements: T[] = [value];
    for (let i = 1; i < length; i++) {
        if (initFunction) {
            elements.push(initFunction(i, elements[i - 1]));
        } else {
            elements.push(value);
        }
    }

    return elements;
}
